import { CacheKeys } from "../constants/CacheKeys";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ApiMethods } from "../models/ApiMethods";
import { ViewDefinition } from "../models/Field";
import {
  CreateCloneImageRequest,
  CreateImageRequest,
  Image,
  MassCreateImageDraftRequest,
  MassCreateImageDraftResponse,
  MassPublishImageRequest,
  MassPublishImageResponse,
  PublishImageRequest,
  UpdateImagesRequest,
} from "../models/Image";
import { ThumbnailsOption } from "../models/ThumbnailOption";

import {
  ApiGetHook,
  ApiMutateHook,
  useApiGet,
  useApiMutate,
} from "@skanska/vsa-client-auth";
import { QueryFilter } from "../models/QueryFilter";
import { ApiQuery, useApiQuery } from "./useApiQuery";

/**
 * Get all chapter images with basic metadata (IDs and names)
 */
export function useGetAllChapterImageIds(): ApiGetHook<ThumbnailsOption[]> {
  return useApiGet<ThumbnailsOption[]>(
    `${ApiEndpoints.ImageChapters}/ids`,
    null,
    "chapter-image-ids"
  );
}

/**
 * Get all chapter images with full metadata
 */
export function useGetAllChapterImages(): ApiGetHook<Image[]> {
  return useApiGet<Image[]>(
    `${ApiEndpoints.ImageChapters}`,
    null,
    "chapter-images"
  );
}

/**
 * Get a specific chapter image with full metadata
 */
export function useGetChapterImage(id: string): ApiGetHook<Image> {
  return useApiGet<Image>(
    `${ApiEndpoints.ImageChapters}`,
    id,
    `chapter-image-${id}`
  );
}

/**
 * Create a new chapter image
 */
export function useCreateChapterImage(): ApiMutateHook<
  Image,
  CreateImageRequest
> {
  return useApiMutate<Image, CreateImageRequest>(
    ApiEndpoints.ImageChapters,
    ApiMethods.Post,
    null,
    null,
    ["chapter-images", "chapter-image-ids", ApiEndpoints.ImageChapterNames],
    null,
    true
  );
}

/**
 * Update an existing chapter image
 */
export function useUpdateChapterImage(
  id: string
): ApiMutateHook<Image, FormData> {
  return useApiMutate<Image, FormData>(
    ApiEndpoints.ImageChapters,
    ApiMethods.Patch,
    null,
    null,
    ["chapter-images", "chapter-image-ids", ApiEndpoints.ImageChapterNames],
    [`chapter-image-${id}`],
    true
  );
}

/**
 * Delete a chapter image
 */
export function useDeleteChapterImage(id: string): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    ApiEndpoints.ImageChapters,
    ApiMethods.Delete,
    id,
    null,
    [
      `chapter-image-${id}`,
      "chapter-images",
      "chapter-image-ids",
      ApiEndpoints.ImageChapterNames,
    ]
  );
}

/**
 * Get a specific draft image
 */
export function useGetDraft(versionId: string): ApiGetHook<Image> {
  return useApiGet<Image>(
    ApiEndpoints.ImageDrafts,
    versionId,
    `image-${versionId}`
  );
}

/**
 * Create a new draft image
 */
export function useCreateDraft(
  imageId?: string
): ApiMutateHook<Image, CreateImageRequest> {
  return useApiMutate<Image, CreateImageRequest>(
    ApiEndpoints.ImageDrafts,
    ApiMethods.Post,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageList,
      `image-${imageId}-hasdraft`,
      ApiEndpoints.ImageActivityNames,
    ],
    null,
    true
  );
}

/**
 * Update a draft image
 */
export function useUpdateDraft(
  versionId: string
): ApiMutateHook<Image, FormData> {
  return useApiMutate<Image, FormData>(
    ApiEndpoints.ImageDrafts,
    ApiMethods.Patch,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageArchivedList,
      `image-${versionId}`,
      ApiEndpoints.ImageActivityNames,
    ],
    null,
    true
  );
}

/**
 * Delete a draft image
 */
export function useDeleteDraft(
  versionId: string,
  imageId: string
): ApiMutateHook<void, void> {
  return useApiMutate<void, void>(
    ApiEndpoints.ImageDrafts,
    ApiMethods.Delete,
    versionId,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageList,
      `image-${imageId}-hasdraft`,
      ApiEndpoints.ImageActivityNames,
    ],
    [`image-${imageId}`]
  );
}

/**
 * Get all published images with pagination and search. imagesOnly filters out non-image files.
 */
export function useGetAllPublished(
  defaultSortBy: string,
  defaultSortAsc: boolean,
  defaultFilters?: QueryFilter[],
  pageSize: number = 20,
  imagesOnly: boolean = true
): ApiQuery<Image> {
  const endpoint = imagesOnly
    ? `${ApiEndpoints.ImageCurrent}?imagesOnly=true`
    : ApiEndpoints.ImageCurrent;
  return useApiQuery<Image>(
    endpoint,
    defaultSortBy,
    defaultSortAsc,
    CacheKeys.Image.ImageList,
    defaultFilters,
    pageSize
  );
}

/**
 * Get a specific published image
 */
export function useGetPublished(versionId: string): ApiGetHook<Image> {
  return useApiGet<Image>(ApiEndpoints.Image, versionId, [
    CacheKeys.Image.SingleImage,
    { imageId: versionId },
  ]);
}

/**
 * Publish a draft image
 */
export function usePublishImage(
  versionId?: string,
  imageId?: string
): ApiMutateHook<Image, PublishImageRequest> {
  return useApiMutate<Image, PublishImageRequest>(
    ApiEndpoints.ImagePublished,
    ApiMethods.Post,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageList,
      CacheKeys.Image.ImageArchivedList,
      `image-${imageId}-hasdraft`,
      `image-history-${imageId}`,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
      CacheKeys.Image.ImageList,
    ],
    [`image-${versionId}`]
  );
}

/**
 * Publish multiple draft images with a shared revision comment.
 */
export function useMassPublishImageDrafts(): ApiMutateHook<
  MassPublishImageResponse,
  MassPublishImageRequest
> {
  return useApiMutate<MassPublishImageResponse, MassPublishImageRequest>(
    ApiEndpoints.ImageMassPublish,
    ApiMethods.Post,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      ApiEndpoints.ImageCurrent,
      CacheKeys.Image.ImageList,
      CacheKeys.Image.ImageArchivedList,
      ApiEndpoints.ImageActivityNames,
    ],
    null
  );
}

/**
 * Save the pending edits on a draft image and publish it in a single request
 */
export function useSaveAndPublishImageDraft(
  versionId?: string,
  imageId?: string
): ApiMutateHook<Image, FormData> {
  return useApiMutate<Image, FormData>(
    ApiEndpoints.ImageDraftsSaveAndPublish,
    ApiMethods.Post,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageList,
      CacheKeys.Image.ImageArchivedList,
      `image-${imageId}-hasdraft`,
      `image-history-${imageId}`,
      ApiEndpoints.ImageActivityNames,
      CacheKeys.Activity.SingleActivity,
      CacheKeys.Activity.ActivityDraft,
    ],
    [`image-${versionId}`],
    true
  );
}

/**
 * Get current published version by ImageId
 */
export function useGetCurrentVersion(imageId: string): ApiGetHook<Image> {
  return useApiGet<Image>(ApiEndpoints.ImageCurrent, imageId, [
    CacheKeys.Image.SingleImage,
    { imageId: imageId },
  ]);
}

/**
 * Check if an image has a draft version
 */
export function useGetCurrentDraftVersion(imageId: string): ApiGetHook<Image> {
  return useApiGet<Image>(
    `${ApiEndpoints.ImageCurrent}/${imageId}/hasdraft`,
    null,
    `image-${imageId}-hasdraft`
  );
}

/**
 * Create a draft from an existing published image
 */
export function useCloneDraftFromPublished(
  imageId: string
): ApiMutateHook<Image, CreateCloneImageRequest> {
  const keys = [
    ApiEndpoints.ImageDrafts,
    ApiEndpoints.ImageCurrent,
    CacheKeys.Image.ImageList,
    `image-${imageId}-hasdraft`,
    ApiEndpoints.ImageActivityNames,
  ];
  return useApiMutate<Image, CreateCloneImageRequest>(
    ApiEndpoints.ImageDraftsClone,
    ApiMethods.Post,
    null,
    null,
    keys,
    null
  );
}

/**
 * Create drafts for multiple published images in a single operation
 */
export function useMassCreateImageDrafts(): ApiMutateHook<
  MassCreateImageDraftResponse,
  MassCreateImageDraftRequest
> {
  const keys = [
    ApiEndpoints.ImageDrafts,
    ApiEndpoints.ImageCurrent,
    CacheKeys.Image.ImageList,
    ApiEndpoints.ImageActivityNames,
  ];
  return useApiMutate<
    MassCreateImageDraftResponse,
    MassCreateImageDraftRequest
  >(ApiEndpoints.ImageMassCreateDraft, ApiMethods.Post, null, null, keys, null);
}

/**
 * Get image history
 */
export function useGetImageHistory(imageId: string): ApiGetHook<Image[]> {
  return useApiGet<Image[]>(
    ApiEndpoints.ImageHistory,
    imageId,
    `image-history-${imageId}`
  );
}

/**
 * Get full image history
 */
export function useGetImageFullHistory(imageId: string): ApiGetHook<Image[]> {
  return useApiGet<Image[]>(
    ApiEndpoints.ImageFullHistory,
    imageId,
    `image-history-${imageId}`
  );
}

/**
 * Get image fields view definition
 */
export function useGetImageFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    `${ApiEndpoints.ImageFields}`,
    null,
    "image-fields"
  );
}

export function useGetImageQuickEditFields(): ApiGetHook<ViewDefinition> {
  return useApiGet<ViewDefinition>(
    ApiEndpoints.ImageQuickEditFields,
    null,
    "image-quick-edit-fields"
  );
}

export function useGetDraftAndPublishedFileNames(): ApiGetHook<string[]> {
  return useApiGet<string[]>(ApiEndpoints.ImageActivityNames);
}

export function useGetCurrentPublishedChapterNames(): ApiGetHook<string[]> {
  return useApiGet<string[]>(ApiEndpoints.ImageChapterNames);
}

export function useUpdateImageDrafts(): ApiMutateHook<
  Image,
  UpdateImagesRequest
> {
  return useApiMutate<Image, UpdateImagesRequest>(
    ApiEndpoints.ImageDraftsQuickEdit,
    ApiMethods.Patch,
    null,
    null,
    [
      ApiEndpoints.ImageDrafts,
      CacheKeys.Image.ImageList,
      CacheKeys.Image.ImageArchivedList,
      ApiEndpoints.ImageActivityNames,
    ]
  );
}
